import {
  useQuery
} from "@tanstack/react-query";
import {
  ArrowRight,
  Play
} from "lucide-react";
import {
  Link
} from "react-router";

import {
  getHomeHero,
  getPublicHomeHeroImageUrl
} from "../../services/catalog";

import {
  HeroSection
} from "./HeroSection";

export function CmsHeroSection() {
  const heroQuery = useQuery({
    queryKey: [
      "homepage",
      "hero"
    ],

    queryFn:
      getHomeHero,

    staleTime:
      5 * 60 * 1000
  });

  /*
   * Preserve the current production Hero while:
   * - the CMS request is loading
   * - no active CMS Hero exists
   * - the CMS API temporarily fails
   */
  if (
    heroQuery.isLoading ||
    heroQuery.isError ||
    !heroQuery.data
  ) {
    return <HeroSection />;
  }

  const hero =
    heroQuery.data;

  const desktopImage =
    hero.desktopImageKey
      ? getPublicHomeHeroImageUrl(
          hero.id,
          "desktop",
          hero.desktopImageKey
        )
      : null;

  const mobileImage =
    hero.mobileImageKey
      ? getPublicHomeHeroImageUrl(
          hero.id,
          "mobile",
          hero.mobileImageKey
        )
      : desktopImage;

  const hasImage =
    Boolean(
      desktopImage ||
      mobileImage
    );

  return (
    <section className="relative isolate overflow-hidden bg-[#efe5d8]">
      {hasImage && (
        <picture className="absolute inset-0 -z-20">
          {mobileImage && (
            <source
              media="(max-width: 767px)"
              srcSet={mobileImage}
            />
          )}

          {desktopImage && (
            <img
              src={desktopImage}
              alt=""
              className="h-full w-full object-cover"
              fetchPriority="high"
              decoding="async"
            />
          )}
        </picture>
      )}

      {hasImage && (
        <div
          className="absolute inset-0 -z-10 bg-black"
          style={{
            opacity:
              hero.overlayOpacity /
              100
          }}
        />
      )}

      {!hasImage && (
        <div className="absolute inset-0 -z-20 bg-gradient-to-br from-[#f3eadf] via-[#efe5d8] to-[#dfcbb8]" />
      )}

      <div className="rini-container">
        <div className="flex min-h-[650px] items-center py-20 md:min-h-[620px] sm:min-h-[700px] lg:min-h-[760px]">
          <div
            className={[
              "max-w-3xl",
              hasImage
                ? "text-white"
                : "text-[#38251e]"
            ].join(" ")}
          >
            {hero.eyebrow && (
              <p
                className={[
                  "text-sm font-semibold uppercase tracking-[0.22em]",
                  hasImage
                    ? "text-white/85"
                    : "text-[#70845b]"
                ].join(" ")}
              >
                {hero.eyebrow}
              </p>
            )}

            <h1 className="mt-5 font-serif text-5xl font-semibold leading-[0.98] sm:text-6xl lg:text-4xl sm:text-5xl lg:text-6xl">
              {hero.title}
            </h1>

            {hero.subtitle && (
              <p
                className={[
                  "mt-7 max-w-2xl text-base leading-7 sm:text-lg sm:leading-8",
                  hasImage
                    ? "text-white/90"
                    : "text-[#665f58]"
                ].join(" ")}
              >
                {hero.subtitle}
              </p>
            )}

            <div className="mt-9 flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center sm:gap-4">
              {hero.primaryCtaText &&
                hero.primaryCtaUrl && (
                  <Link
                    to={
                      hero.primaryCtaUrl
                    }
                    className="inline-flex items-center gap-2 rounded-full bg-[#5e3b2c] px-7 py-3.5 font-semibold text-white transition hover:gap-3 hover:bg-[#4d3024]"
                  >
                    {
                      hero.primaryCtaText
                    }

                    <ArrowRight
                      size={18}
                    />
                  </Link>
                )}

              {hero.secondaryCtaText &&
                hero.secondaryCtaUrl && (
                  <Link
                    to={
                      hero.secondaryCtaUrl
                    }
                    className={[
                      "inline-flex items-center gap-2 rounded-full border px-7 py-3.5 font-semibold transition",
                      hasImage
                        ? "border-white/60 bg-white/10 text-white backdrop-blur-sm hover:bg-white/20"
                        : "border-[#5e3b2c]/25 text-[#5e3b2c] hover:bg-[#5e3b2c]/5"
                    ].join(" ")}
                  >
                    <Play
                      size={16}
                    />

                    {
                      hero.secondaryCtaText
                    }
                  </Link>
                )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
